import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AgentIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef} from 'react'

import {AgentSessionsSidePanel} from './AgentSessionsSidePanel'
import {DockedPanel, useDockedPanelActions, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {Repository} from '@github-ui/current-repository'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

export interface OpenAgentsSidePanelButtonProps {
  repository: Repository
  pullRequestNumber: number
}

export const ICON_BUTTON_LABELS = {
  open: 'Open agent sessions',
  close: 'Close agent sessions',
} as const

export function OpenAgentsPanelButton({repository, pullRequestNumber}: OpenAgentsSidePanelButtonProps) {
  const isCodingAgentLogsInPrEnabled = isFeatureEnabled('coding_agent_logs_in_pr')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.AgentSessions)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)
  const actions = useDockedPanelActions()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  if (!isCodingAgentLogsInPrEnabled) {
    return null
  }

  const iconButtonLabel = isDockedPanelOpen ? ICON_BUTTON_LABELS.close : ICON_BUTTON_LABELS.open

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        boundaryName="OpenAgentsSidePanelButton"
        fallback={
          <IconButton
            inactive
            aria-label="The agent sessions panel cannot currently be opened."
            icon={AgentIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <IconButton
          ref={toggleSidePanelRef}
          aria-label={iconButtonLabel}
          icon={AgentIcon}
          size="small"
          variant="invisible"
          onClick={() => {
            sendPullRequestAnalyticsEvent('agent_sessions_side_panel.open', 'AGENT_SESSIONS_SIDE_PANEL_BUTTON')
            actions.togglePanel('agentSessions', {returnFocusTo: toggleSidePanelRef.current})
          }}
        />
        <AgentSessionsSidePanel
          isOpen={isDockedPanelOpen}
          repository={repository}
          pullRequestNumber={pullRequestNumber}
        />
      </ErrorBoundary>
    </div>
  )
}
